import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Copy, Mail, Lock, CheckCircle2, Trash2, CalendarPlus, RefreshCw } from "lucide-react";
import { buildIcs, downloadIcs } from "../lib/calendar";
import { copyToClipboard } from "../lib/clipboard";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import CalibrationTracker from "../components/CalibrationTracker";
import ConversationHeldCard from "../components/ConversationHeldCard";

export default function CalibrationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Default follow-up: 14 days from now at 10:00 local
  const defaultDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    d.setHours(10, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [followUp, setFollowUp] = useState(defaultDate());
  const [resending, setResending] = useState(false);

  useEffect(() => {
    api.get(`/calibrations/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(apiError(e, "Not found")));
  }, [id]);

  if (!c) return <div className="p-10 font-mono-cal text-xs">[LOADING…]</div>;

  const shareUrl = `${window.location.origin}/e/${c.access_token}`;
  const both = c.employee_submitted && c.manager_submitted;

  const copy = async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) toast.success("Link copied");
    else toast.error("Copy blocked by browser — please select and copy manually");
  };
  const mailto = () => {
    const subject = encodeURIComponent(`Performance calibration: ${c.title}`);
    const body = encodeURIComponent(
      `Hi ${c.employee_name},\n\nIt's time for our ${c.period_label.toLowerCase()} calibration — "${c.title}".\n\n` +
      `Please use the secret link below to complete your self-assessment. You can also add personal goals before submitting. ` +
      `My rating will stay sealed until you submit, and vice versa.\n\n${shareUrl}\n\nThanks,\n${c.manager_name}`
    );
    window.location.href = `mailto:${c.employee_email}?subject=${subject}&body=${body}`;
  };

  const del = async () => {
    try {
      await api.delete(`/calibrations/${id}`);
      toast.success("Calibration deleted");
      nav(`/profiles/${c.profile_id}`);
    } catch (err) { toast.error(apiError(err, "Delete failed")); }
  };

  const addToCalendar = () => {
    if (!followUp) { toast.error("Pick a follow-up date first"); return; }
    const shareLink = `${window.location.origin}/e/${c.access_token}`;
    const ics = buildIcs({
      uid: c.id,
      title: `Follow up · ${c.title} (${c.employee_name})`,
      description:
        `1:1 calibration debrief with ${c.employee_name} (${c.employee_email}).\n` +
        `Period: ${c.period_label}.\n\n` +
        `Employee self-assessment link: ${shareLink}\n\n` +
        `Open CALIBRATE to review the comparison overlay before the meeting.`,
      startLocal: followUp,
      durationMinutes: 60,
    });
    downloadIcs(`calibrate-followup-${c.id.slice(0, 8)}`, ics);
    toast.success("Calendar invite downloaded — open it to add to Outlook/Google/Apple");
  };

  const resendInvite = async () => {
    setResending(true);
    try {
      await api.post(`/calibrations/${id}/resend-invite`);
      toast.success(`Invite resent to ${c.employee_email}`);
      // Refresh to update "last_invite_sent_at"
      const r = await api.get(`/calibrations/${id}`);
      setC(r.data);
    } catch (err) {
      toast.error(apiError(err, "Resend failed"));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
      <Link to={`/profiles/${c.profile_id}`} className="font-mono-cal text-xs text-[#666] hover:text-[#FF4F00]" data-testid="back-profile">← PROFILE</Link>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#666]">[CALIBRATION] {c.period_label}</div>
          <h1 className="font-display font-bold text-5xl tracking-tight mt-2">{c.title}</h1>
          <div className="text-[#666] mt-1">{c.employee_name} · {c.employee_email}</div>
        </div>
        <button onClick={() => setConfirmDelete(true)} className="cal-btn cal-btn-ghost" data-testid="cal-delete-btn"><Trash2 size={14} /> Delete</button>
      </div>

      <div className="mb-6">
        <CalibrationTracker cal={c} onDetailPage />
      </div>

      {both && <ConversationHeldCard cal={c} onChange={(patch) => setC((prev) => ({ ...prev, ...patch }))} />}

      {/* Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <StatusRow label="Employee self-assessment" done={c.employee_submitted} testid="status-employee" />
        <StatusRow label="Your assessment" done={c.manager_submitted} testid="status-manager" />
      </div>

      {/* Magic-link share */}
      <div className="cal-card cal-shadow p-8 mb-8" data-testid="share-card">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[ SECRET INVITE LINK ]</div>
        <h2 className="font-display font-bold text-2xl mb-2">Share with {c.employee_name}</h2>
        <p className="text-[#666] text-sm mb-4">They'll also receive this link by email. No account required — they can add goals and complete their self-rating directly from the link.</p>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch">
          <input readOnly value={shareUrl} className="cal-input font-mono-cal text-xs flex-1" data-testid="share-url" onFocus={(e) => e.target.select()} />
          <button onClick={copy} className="cal-btn" data-testid="copy-link-btn"><Copy size={14} /> Copy</button>
          <button onClick={mailto} className="cal-btn cal-btn-ghost" data-testid="mailto-btn"><Mail size={14} /> Open mail</button>
        </div>
        {!c.employee_submitted && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#111]/15 pt-4">
            <div className="text-xs text-[#666]">
              {c.last_invite_sent_at ? (
                <span className="font-mono-cal">Last email sent {new Date(c.last_invite_sent_at).toLocaleString()}</span>
              ) : (
                <span>Nudge them if they haven't responded yet.</span>
              )}
            </div>
            <button onClick={resendInvite} disabled={resending} className="cal-btn cal-btn-ghost whitespace-nowrap" data-testid="resend-invite-btn">
              <RefreshCw size={14} /> {resending ? "Sending…" : "Resend invite email"}
            </button>
          </div>
        )}
      </div>

      {/* Calendar follow-up */}
      <div className="cal-card p-8 mb-8" data-testid="calendar-card">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2 flex items-center gap-2">
          <CalendarPlus size={12} /> [ FOLLOW-UP REMINDER ]
        </div>
        <h2 className="font-display font-bold text-2xl mb-2">Block time for the 1:1</h2>
        <p className="text-[#666] text-sm mb-4">
          Download a calendar invite that drops straight into Outlook, Google Calendar, or Apple Calendar — pre-filled with the share link and meeting context.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch">
          <input
            type="datetime-local"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            className="cal-input sm:max-w-xs"
            data-testid="followup-datetime"
          />
          <button onClick={addToCalendar} className="cal-btn" data-testid="add-calendar-btn">
            <CalendarPlus size={14} /> Add follow-up to calendar
          </button>
        </div>
      </div>

      {/* Action */}
      <div className="cal-card p-8">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2">[ YOUR TURN ]</div>
        {c.manager_submitted ? (
          <>
            <h2 className="font-display font-bold text-2xl mb-2">Your rating is sealed</h2>
            <p className="text-[#666] mb-4">{both ? "Both sides have submitted." : "Waiting on the employee to submit their self-assessment."}</p>
            {both && <Link to={`/calibrations/${id}/comparison`} className="cal-btn" data-testid="open-comparison-btn">View comparison →</Link>}
          </>
        ) : (
          <>
            <h2 className="font-display font-bold text-2xl mb-2">Complete your manager rating</h2>
            <p className="text-[#666] mb-4">Rate each task and any goals the employee has added — blind. The employee's ratings stay hidden until both of you submit.</p>
            <Link to={`/calibrations/${id}/rate`} className="cal-btn" data-testid="rate-now-btn"><Lock size={14} /> Rate now (blind)</Link>
          </>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDeleteModal
          open={true}
          title="Delete calibration?"
          subjectLabel="This removes"
          subjectName={c.title}
          warning="Every rating and note on this calibration will be destroyed. This cannot be undone."
          requiredWord="DELETE"
          confirmLabel="Delete calibration"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={del}
        />
      )}
    </div>
  );
}

function StatusRow({ label, done, testid }) {
  return (
    <div className="flex items-center justify-between p-4 border border-[#111] bg-white" data-testid={testid}>
      <span className="font-display font-medium">{label}</span>
      {done ? <span className="cal-badge cal-badge-unlocked"><CheckCircle2 size={12} /> Submitted</span>
            : <span className="cal-badge cal-badge-waiting"><Lock size={12} /> Pending</span>}
    </div>
  );
}
